/**
 * Development session provider.
 *
 * Active only when the server runs with `ALLOW_DEV_AUTH=1`. It asks for an email address and
 * uses it as the bearer credential, which lets the whole app — including multi-user sharing —
 * be exercised locally without Clerk credentials.
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { useStore } from '../data/context.js';
import { SessionContextProvider, SessionStatus, type SessionContextValue } from './session.js';

/** Where the impersonated address is kept between reloads. */
const STORAGE_KEY = 'anwesenheit.dev-email';

/** Reads the stored development address, or `null` when none was chosen yet. */
function readStoredEmail(): string | null {
  const stored = globalThis.localStorage?.getItem(STORAGE_KEY) ?? null;
  return stored !== null && stored.includes('@') ? stored : null;
}

/** Provides a session derived from a locally chosen email address. */
export function DevSession({ children }: { children: ReactNode }): ReactNode {
  const store = useStore();
  const [email, setEmail] = useState<string | null>(readStoredEmail);

  const signIn = useCallback((address: string): void => {
    const normalized = address.trim().toLowerCase();
    globalThis.localStorage?.setItem(STORAGE_KEY, normalized);
    setEmail(normalized);
  }, []);

  const signOut = useCallback(async (): Promise<void> => {
    globalThis.localStorage?.removeItem(STORAGE_KEY);
    await store.forget();
    setEmail(null);
  }, [store]);

  const value = useMemo((): SessionContextValue => {
    if (email === null) {
      return {
        status: SessionStatus.SignedOut,
        session: null,
        getToken: async () => null,
        signOut,
      };
    }
    return {
      status: SessionStatus.Active,
      session: { userId: `dev_${email}`, email },
      getToken: async () => email,
      signOut,
    };
  }, [email, signOut]);

  if (email === null) return <DevSignInForm onSubmit={signIn} />;
  return <SessionContextProvider value={value}>{children}</SessionContextProvider>;
}

/** Asks for the address to impersonate during development. */
function DevSignInForm({ onSubmit }: { onSubmit: (email: string) => void }): ReactNode {
  const [value, setValue] = useState('');
  const valid = value.includes('@');

  return (
    <main className="mx-auto flex min-h-full w-full max-w-md flex-col justify-center gap-6 px-6">
      <div>
        <p className="text-tiny font-semibold tracking-wide text-accent-text uppercase">Development mode</p>
        <h1 className="mt-2 text-display font-bold">Anwesenheit</h1>
        <p className="mt-2 text-ink-2">
          The server is running without Clerk. Enter any address to act as that account.
        </p>
      </div>
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (valid) onSubmit(value);
        }}
      >
        <label className="text-tiny font-semibold text-ink-2" htmlFor="dev-email">Email address</label>
        <input
          id="dev-email"
          type="email"
          autoComplete="email"
          className="h-12 rounded-control border border-line-strong bg-surface px-3 text-base"
          value={value}
          onChange={(event) => setValue(event.target.value)}
          placeholder="you@example.com"
        />
        <button
          type="submit"
          disabled={!valid}
          className="h-12 rounded-control bg-brand px-4 font-semibold text-on-brand disabled:opacity-40"
        >
          Continue
        </button>
      </form>
    </main>
  );
}
